// audience_stats.js — audience-statistics modal (stats_ui_plan_2026-10-24.md §3/§5).
//
// Self-contained portable component (plan §3.1: no framework dependency —
// no Bootstrap, no Turbo; namespaced bgm-stats-* classes + own CSS file).
//
// Behavior (plan §3, owner decisions 2026-10-24):
//   1. Click on [data-bgm-stats] → modal opens INSTANTLY: shell + all
//      static explanatory copy render from the template below; only the
//      DATA AREA shows a loading state.
//   2. In parallel the payload is fetched (POST, same-origin, CSRF).
//   3. chart.js is dynamically imported on first open; chart drawn into
//      the data area. Failure → calm error state inside the modal; the
//      article page itself is never affected.
(function () {
  "use strict";

  var SEGMENT_LABELS = {
    human_readers: "Human readers",
    ai_systems: "AI systems",
    search_engines: "Search engines",
    unverified_browsers: "Automated readers",
    other_bots: "Other bots",
    all: "All visits"
  };
  var SEGMENT_BLURBS = {
    human_readers: "People reading the article in a browser or app.",
    ai_systems: "AI assistants and AI search engines — such as those behind ChatGPT, Claude or Perplexity — reading the content to answer their users.",
    search_engines: "Crawlers building search indexes.",
    unverified_browsers: "Automated readers using browser-like software (typical of data and research systems).",
    other_bots: "Monitoring and miscellaneous automated traffic."
  };

  function csrfToken() {
    var meta = document.querySelector('meta[name="csrf-token"]');
    return meta ? meta.content : "";
  }

  // ── Modal shell (static template — instant, no network) ──────────────
  function buildModal(endpoint, title) {
    var overlay = document.createElement("div");
    overlay.className = "bgm-stats-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.setAttribute("aria-label", "Audience statistics");
    overlay.innerHTML =
      '<div class="bgm-stats-modal">' +
      '  <header class="bgm-stats-header">' +
      '    <div>' +
      '      <p class="bgm-stats-eyebrow">Audience statistics</p>' +
      '      <h2 class="bgm-stats-title"></h2>' +
      "    </div>" +
      '    <button type="button" class="bgm-stats-close" aria-label="Close statistics">&times;</button>' +
      "  </header>" +
      '  <div class="bgm-stats-body">' +
      '    <section class="bgm-stats-intro">' +
      "      <p>This page shows how " +
      "        <strong>readers and AI systems</strong> discovered and consumed this" +
      "        content during its first weeks after publication.</p>" +
      '      <p class="bgm-stats-note">Curious who reads BriefGlance coverage? Every line above counts a different' +
      '        kind of audience — see the explanations below the chart.</p>' +
      "    </section>" +
      '    <div class="bgm-stats-data-area" aria-live="polite">' +
      '      <div class="bgm-stats-loading" data-loading>' +
      '        <span class="bgm-stats-spinner" aria-hidden="true"></span>' +
      "        <p>Loading the numbers…</p>" +
      "      </div>" +
      "    </div>" +
      '    <section class="bgm-stats-explainer">' +
      '      <h3>What the lines mean</h3>' +
      '      <ul class="bgm-stats-segments">' +
      segmentListHtml() +
      "      </ul>" +
      '      <p class="bgm-stats-methodology">Method: visits are counted from our server logs and classified by' +
      "        reader type; the black line is the total of all visits. The gray line, when shown, is what a" +
      "        typical article on this site achieves — computed weekly from comparable recent articles." +
      "        Visits are counted per day since publication and added up (cumulative).</p>" +
      "    </section>" +
      "  </div>" +
      "</div>";
    overlay.querySelector(".bgm-stats-title").textContent = title;
    overlay.dataset.endpoint = endpoint;
    return overlay;
  }

  function segmentListHtml() {
    return Object.keys(SEGMENT_LABELS).map(function (id) {
      return (
        '<li><span class="bgm-stats-seg-label" data-seg="' + id + '">' + SEGMENT_LABELS[id] +
        "</span>" +
        '<span class="bgm-stats-seg-blurb">' + (SEGMENT_BLURBS[id] || "") + "</span></li>"
      );
    }).join("");
  }

  // ── Modal open/close (overlay click, Esc, focus containment) ─────────
  var openModal = null;
  var lastFocused = null;
  var chartSrc = null;

  function open(endpoint, title, chartScriptSrc) {
    if (openModal) close();
    lastFocused = document.activeElement;
    chartSrc = chartScriptSrc;
    openModal = buildModal(endpoint, title);
    document.body.appendChild(openModal);
    document.body.classList.add("bgm-stats-no-scroll");
    openModal.addEventListener("click", onOverlayClick);
    document.addEventListener("keydown", onKeydown, true);
    var closeBtn = openModal.querySelector(".bgm-stats-close");
    closeBtn.addEventListener("click", close);
    closeBtn.focus();
    // Data fetch starts AFTER the modal is visible (plan §3).
    fetchData(openModal, endpoint);
  }

  function close() {
    if (!openModal) return;
    document.removeEventListener("keydown", onKeydown, true);
    document.body.classList.remove("bgm-stats-no-scroll");
    openModal.remove();
    openModal = null;
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function onOverlayClick(e) {
    if (e.target === openModal) close(); // click on backdrop, not children
  }

  function onKeydown(e) {
    if (e.key === "Escape") close();
  }

  // ── Data fetch (POST, same-origin, CSRF) ──────────────────────────────
  function fetchData(modal, endpoint) {
    fetch(endpoint, {
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken(),
        "Accept": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      credentials: "same-origin"
    })
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.json();
      })
      .then(function (payload) {
        renderData(modal, payload);
      })
      .catch(function () {
        renderError(modal);
      });
  }

  function dataArea(modal) {
    return modal.querySelector(".bgm-stats-data-area");
  }

  function renderError(modal) {
    dataArea(modal).innerHTML =
      '<div class="bgm-stats-error">' +
      "  <p>Statistics are temporarily unavailable.</p>" +
      '  <button type="button" class="bgm-stats-retry">Try again</button>' +
      "</div>";
    dataArea(modal)
      .querySelector(".bgm-stats-retry")
      .addEventListener("click", function () {
        setLoading(modal);
        fetchData(modal, modal.dataset.endpoint);
      });
  }

  function setLoading(modal) {
    dataArea(modal).innerHTML =
      '<div class="bgm-stats-loading">' +
      '  <span class="bgm-stats-spinner" aria-hidden="true"></span>' +
      "  <p>Loading the numbers…</p>" +
      "</div>";
  }

  // ── Data rendering (numbers + chart) ─────────────────────────────────
  function renderData(modal, payload) {
    var area = dataArea(modal);
    var headline = buildHeadline(payload);
    var chartId = "bgm-stats-chart-" + Date.now();

    area.innerHTML =
      '<p class="bgm-stats-headline"></p>' +
      '<figure class="bgm-stats-figure"><canvas id="' + chartId + '"></canvas></figure>' +
      '<p class="bgm-stats-notes"></p>' +
      '<p class="bgm-stats-asof">Data as of <time></time>.</p>';

    area.querySelector(".bgm-stats-headline").textContent = headline;
    area.querySelector(".bgm-stats-notes").textContent = (payload.notes || []).join(" ");
    var timeEl = area.querySelector("time");
    if (timeEl) timeEl.textContent = new Date(payload.generated_at).toLocaleString();

    drawChart(chartId, payload);
  }

  // Headline framing (plan §7.5 "expected volume from day one", owner
  // 2026-10-25): young content speaks to POTENTIAL — what the typical
  // item achieves over the full window; mature content gets the honest
  // "X× the typical" comparison.
  function buildHeadline(payload) {
    var all = (payload.series || []).find(function (s) { return s.id === "all"; });
    if (!all || !all.cumulative.length) return "";
    var ref = (payload.series || []).find(function (s) { return s.id === "ref_all"; });
    var days = all.cumulative.length;
    var total = all.cumulative[all.cumulative.length - 1];
    var noun = payload.page_type === "article" ? "article" : "press release";
    var window = payload.ref_days || days;

    // Young: factual days < full window → potential framing.
    if (days < window) {
      if (ref && ref.cumulative.length >= window && ref.cumulative[window - 1] > 0) {
        return "Day " + days + ": " + total.toLocaleString() + " visits so far. Typical " + noun +
          "s reach " + ref.cumulative[window - 1].toLocaleString() + " visits in their first " + window + " days — the gray line shows that expected growth.";
      }
      return "Day " + days + ": " + total.toLocaleString() + " visits so far.";
    }

    // Mature: full-window comparison.
    var text = "By day " + days + " this " + noun + " reached " + total.toLocaleString() + " visits";
    if (ref && ref.cumulative.length >= days && ref.cumulative[days - 1] > 0) {
      var ratio = total / ref.cumulative[days - 1];
      text += " — " + (ratio >= 10 ? Math.round(ratio) : Math.round(ratio * 10) / 10) + "× the typical article";
    }
    return text;
  }

  // chart.js lives in its own bundle (chart_vendor.js); the digested URL
  // comes from the server via the button's data-bgm-stats-chart-src —
  // fetched only when a chart is actually drawn.
  function loadChartLib() {
    if (window.ChartLib) return Promise.resolve(window.ChartLib);
    if (!chartSrc) return Promise.reject(new Error("no chart src"));

    return new Promise(function (resolve, reject) {
      var s = document.createElement("script");
      s.type = "module";
      s.src = chartSrc;
      s.onload = function () {
        window.ChartLib ? resolve(window.ChartLib) : reject(new Error("ChartLib missing"));
      };
      s.onerror = function () { reject(new Error("chart bundle failed")); };
      document.head.appendChild(s);
    });
  }

  function drawChart(canvasId, payload) {
    loadChartLib()
      .then(function (lib) {
        var Chart = lib.Chart;
        var labels = dateLabels(payload);
        var datasets = (payload.series || []).map(function (s) {
          return {
            label: s.label,
            data: padTo(s.cumulative, labels.length),
            borderColor: s.color,
            backgroundColor: s.color,
            borderWidth: s.kind === "reference" ? 2 : 2.5,
            borderDash: s.kind === "reference" ? [6, 4] : undefined,
            pointRadius: 0,
            pointHitRadius: 6,
            tension: 0.25,
            fill: false,
            spanGaps: false
          };
        });

        new Chart(document.getElementById(canvasId), {
          type: "line",
          data: { labels: labels, datasets: datasets },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: { duration: 200 },
            scales: {
              y: {
                beginAtZero: true,
                title: { display: true, text: "Cumulative visits" }
              },
              x: {
                ticks: { maxTicksLimit: 8 },
                title: { display: false }
              }
            },
            plugins: {
              legend: {
                position: "bottom",
                labels: { usePointStyle: true, boxWidth: 8 }
              },
              tooltip: {
                mode: "index",
                intersect: false,
                callbacks: {
                  label: function (ctx) {
                    return ctx.dataset.label + ": " + ctx.parsed.y.toLocaleString();
                  }
                }
              }
            }
          }
        });
      })
      .catch(function (err) {
        console.error("bgm-stats: chart.js failed to load", err);
      });
  }

  // Pad a data array to `len` with nulls (chart.js: null = gap) so shorter
  // factual series align correctly against the full-window axis.
  function padTo(arr, len) {
    var out = arr.slice();
    while (out.length < len) out.push(null);
    return out;
  }

  // X axis = factual calendar dates (plan §5): published_day + day_offset.
  // The axis ALWAYS spans the full ref_days window (owner 2026-10-25): even
  // on day 2 the visitor sees the reference line's whole potential growth
  // over 30/60 days — "expected volume from day one" (plan §7.5). The
  // factual line simply stops at today; chart.js leaves the rest empty.
  function dateLabels(payload) {
    var start = new Date(payload.published_day + "T00:00:00Z");
    var longest = (payload.series || []).reduce(function (m, s) {
      return Math.max(m, s.cumulative.length);
    }, 0);
    var days = Math.max(payload.ref_days || 0, longest);
    var labels = [];
    for (var i = 0; i < days; i++) {
      var d = new Date(start);
      d.setUTCDate(d.getUTCDate() + i);
      labels.push(d.toLocaleDateString(undefined, { month: "short", day: "numeric" }));
    }
    return labels;
  }

  // ── Wiring: buttons declared in server markup ────────────────────────
  function init() {
    document.addEventListener("click", function (e) {
      var btn = e.target.closest("[data-bgm-stats]");
      if (!btn) return;
      e.preventDefault();
      var title = btn.getAttribute("data-bgm-stats-title") || document.title;
      open(btn.getAttribute("data-bgm-stats-endpoint"), title,
           btn.getAttribute("data-bgm-stats-chart-src"));
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
